"use client"

import { Link } from "react-router-dom"
import { ArrowUpRight, Trophy } from "lucide-react"
import { Button } from "@/components/ui/button"
import { usePortal } from "@/hooks/use-portal"
import { motion, AnimatePresence } from "framer-motion"

const HeroSection = () => {
  const { portal } = usePortal()

  const content = {
    tspsc: {
      location: "TSPSC / Hyderabad",
      title: "The Best Coaching for TSPSC Exams.",
      subtitle: "Prepare with Komma Rohith Sir to clear Group 1 and Group 2 exams with confidence.",
      visual: "/Telangana_Secretariat.jpg",
      verified: "45+ Verified Selections"
    },
    appsc: {
      location: "APPSC / Andhra Pradesh",
      title: "Top Coaching for APPSC Group 1 & 2.",
      subtitle: "Join the institute that delivers real results. Direct guidance from Komma Rohith Sir.",
      visual: "/Dhyana Buddha Statue- Amaravati-Vijayawada-Andhra Pradesh-hero-hs.jpeg",
      verified: "Highest Selection Ratio"
    }
  }

  const active = content[portal] || content["tspsc"]

  return (
    <section className="relative overflow-hidden border-b border-slate-200 bg-slate-900 min-h-[calc(100svh-112px)] flex items-center">

      {/* Background image — smooth crossfade on switch */}
      <AnimatePresence mode="wait">
        <motion.div
          key={portal + "-bg"}
          className="absolute inset-0 z-0"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.6, ease: "easeInOut" }}
        >
          <img
            src={active.visual}
            alt="Academy Heritage"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-slate-950/60 transition-opacity duration-700" />
          
          {/* Professional Ambient Accents - Indian Administrative Energy */}
          <div className="absolute -bottom-1/4 -left-20 w-[500px] h-[500px] bg-emerald-900/15 blur-[120px] rounded-full mix-blend-screen pointer-events-none" style={{ animationDelay: '2.5s' }} />
          
          <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-slate-950 to-transparent" />
        </motion.div>
      </AnimatePresence>

      {/* Floating content card — slides in from left on initial load, crossfades on portal switch */}
      <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 relative z-10 py-6 lg:py-4 flex justify-start">
        <motion.div
          initial={{ opacity: 0, x: -60 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ 
            duration: 0.8, 
            ease: [0.16, 1, 0.3, 1]
          }}
          className="bg-slate-900/40 w-[85%] max-w-[280px] sm:max-w-none sm:w-[65%] md:w-[60%] lg:w-[40%] xl:w-[36%] px-6 py-6 sm:px-10 lg:px-12 lg:py-10 shadow-2xl border border-white/10 rounded-2xl relative overflow-hidden group/hero-card"
        >
          {/* Institution Dual-Tone Accent Border */}
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-blue-500/0 via-blue-500/40 to-blue-500/0" />
          <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-emerald-500/0 via-emerald-500/40 to-emerald-500/0" />

          {/* Animated Shimmer Line */}
          <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden">
             <div className="absolute -inset-[100%] bg-gradient-to-r from-transparent via-white/[0.05] to-transparent skew-x-12 translate-x-[-100%] group-hover/hero-card:animate-[shimmer_3s_infinite]" style={{ animation: 'shimmer 10s infinite' }} />
          </div>

          <style dangerouslySetInnerHTML={{ __html: `
            @keyframes shimmer {
              0% { transform: translateX(-150%) skewX(-20deg); }
              100% { transform: translateX(150%) skewX(-20deg); }
            }
          `}} />

          {/* Subtle Inner Glow */}
          <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-transparent pointer-events-none" />

          <AnimatePresence mode="wait">
            <motion.div
              key={portal + "-content"}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="space-y-3 lg:space-y-4 text-left flex flex-col items-start"
            >
              <div className="space-y-2 lg:space-y-4 flex flex-col items-start">
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2 justify-start">
                    <p className="text-[8px] lg:text-[10px] font-bold uppercase tracking-[0.4em] text-[#b48c36]">{active.location}</p>
                    <div className="w-4 lg:w-8 h-px bg-[#b48c36]/30 hidden sm:block" />
                  </div>
                  <div className="flex items-center gap-2 justify-start">
                    <div className="bg-white/10 border border-white/10 px-1.5 lg:px-3 py-1 rounded-full flex items-center gap-1.5 lg:gap-2 shadow-inner">
                      <Trophy size={10} className="text-[#b48c36]" />
                      <span className="text-[7px] lg:text-[10px] font-bold uppercase tracking-[0.15em] text-[#b48c36]">{active.verified}</span>
                    </div>
                  </div>
                </div>

                <h1 className="font-serif text-[20px] leading-[1.1] sm:text-[32px] lg:text-[35px] text-white tracking-tight font-medium">
                  {active.title}
                </h1>

                <p className="text-[10px] sm:text-xs lg:text-[14px] font-sans text-white/60 leading-relaxed font-light max-w-[220px] lg:max-w-xs">
                  {active.subtitle}
                </p>
              </div>

              <div className="flex flex-row flex-wrap items-center gap-3 sm:gap-6 pt-0.5 lg:pt-1 w-full lg:w-auto">
                <Button 
                  onClick={() => document.getElementById('testimonials')?.scrollIntoView({ behavior: 'smooth' })}
                  size="sm" 
                  className="bg-primary text-white hover:bg-[#b48c36] rounded-sm px-4 lg:px-8 h-8 lg:h-12 text-[8px] lg:text-[10px] font-bold uppercase tracking-[0.2em] shadow-xl transition-all"
                >
                  View Selections
                </Button>
                <Link 
                  to="/#courses" 
                  onClick={(e) => {
                    if (window.location.pathname === '/') {
                      e.preventDefault();
                      document.getElementById('courses')?.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                  className="text-[8px] lg:text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 hover:text-primary transition-colors flex items-center gap-1 lg:gap-2 group"
                >
                  Programs <ArrowUpRight size={12} className="text-slate-300 group-hover:text-primary transition-all lg:w-[14px] lg:h-[14px]" />
                </Link>
              </div>

              {/* Stats matrix */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 lg:gap-8 pt-3 lg:pt-4 border-t border-white/10 w-full text-left">
                <div className="space-y-0.5">
                  <p className="font-serif text-[18px] lg:text-2xl text-white font-medium">45<span className="text-[8px] lg:text-xs text-[#b48c36] ml-1 font-sans ">posts</span></p>
                  <p className="text-[7px] lg:text-[9px] font-bold uppercase tracking-[0.3em] text-slate-400">Group 1</p>
                </div>
                <div className="space-y-0.5">
                  <p className="font-serif text-[18px] lg:text-2xl text-white font-medium">120<span className="text-[8px] lg:text-xs text-[#b48c36] ml-1 font-sans ">posts</span></p>
                  <p className="text-[7px] lg:text-[9px] font-bold uppercase tracking-[0.3em] text-slate-400">Group 2</p>
                </div>
                <div className="space-y-0.5 hidden sm:block">
                  <p className="font-serif text-[18px] lg:text-2xl text-white font-medium">R1</p>
                  <p className="text-[7px] lg:text-[9px] font-bold uppercase tracking-[0.3em] text-slate-400">Top Rank</p>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  )
}

export default HeroSection
